'use client';
import React, { useMemo } from 'react';
import { I18nextProvider } from 'react-i18next';
import i18next from 'i18next';

import type { Translations } from '@nutralab/api';

interface I18nProviderProps {
    locale: string;
    translations: Translations;
    children: React.ReactNode;
}

/** Gives client components the site's translations (`useTranslation`), loaded on the server for the current locale. */
export function I18nProvider({ locale, translations, children }: I18nProviderProps): React.ReactElement {
    const instance = useMemo(() => {
        const i18n = i18next.createInstance();

        // Resources are inline and `initImmediate: false`, so init finishes synchronously.
        void i18n.init({
            lng: locale,
            fallbackLng: 'en',
            resources: { [locale]: { translation: translations } },
            interpolation: { escapeValue: false },
            keySeparator: '.',
            returnNull: false,
            initImmediate: false,
        });

        return i18n;
    }, [locale, translations]);

    return <I18nextProvider i18n={instance}>{children}</I18nextProvider>;
}
